//#region IMPORTS
import {
  CANCEL_ORDER_SURE,
  SHOW_SUBORDER_INFO,
  CHOOSE_PICKLIST_SUPPLIER,
} from "../../models/_consts";
import {
  fetchFilters,
  ordersSelectors,
  fetchOrdersAsync,
  updateOrderAsync,
  setSelectedOrders,
  setSelectedSubOrder,
} from "../../features/orders/ordersSlice";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { createColumnHelper } from "@tanstack/react-table";
import { ORDERSTATUS, Order } from "../../models/order";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import MainSortableTableCheckBox from "../_layout/MainSortableTableCheckBox";
import OrdersTableControlePanel from "../orders/OrdersTableControlePanel";
import MainTableRowActionRead from "../_layout/MainTableRowActionRead";
import OrdersHandlingTable from "./OrdersHandlingTable";
import OrdersFilterBar from "../orders/OrdersFilterBar";
import SupplierSelect from "../modalTypes/SupplierSelect";
import SubOrderDetail from "../suborderDetail/SubOrderDetail";
import MainContainer from "../_layout/MainContainer";
import PageHeader from "../genericPageHeader/PageHeader";
import AreYouSure from "../modalTypes/AreYouSure";
import MainModal from "../_layout/MainModal";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function OrdersHandlingContainer({
  title = "rituals.orderHistory",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const orders = useAppSelector(ordersSelectors.selectAll);
  const {
    selectedSubOrder,
    filters_loaded,
    selectedOrders,
    orders_loaded,
    selectedOrder,
    filters,
    loading,
  } = useAppSelector((state) => state.orders);
  const { limit, sort, page, include_sub_orders, ...params } = useAppSelector(
    (state) => state.orders.order_params
  );

  // Local state
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  //#endregion

  //#region LOGIC
  // Load filters
  useEffect(() => {
    const fetchFiltering = async () => {
      await dispatch(fetchFilters());
    };
    !filters_loaded && fetchFiltering();
  }, [filters_loaded, dispatch]);

  useEffect(() => {
    if (!orders_loaded) {
      dispatch(setSelectedOrders([]));
    }
  }, [orders_loaded, dispatch]);

  // Memoized select handler
  const selectOrdersFromTableSelectionHandler = useCallback(
    (currentSelectedOrders: Order[]) => {
      dispatch(setSelectedOrders(currentSelectedOrders));
    },
    [dispatch]
  );

  // Change orderstatus shortcut handler
  const changeOrderStatusHandler = async (
    order: Order,
    order_status: string
  ) => {
    try {
      await dispatch(
        updateOrderAsync({
          orderId: order.id,
          values: {
            buyer_id: order.buyer_id,
            comment: order.comment,
            order_code: order.order_code,
            promo_id: order.promo_id,
            order_status,
            order_type: order.order_type,
          },
        })
      );

      // Change status of suborders
      const updateValues = {
        sub_orders: order.sub_orders.map((subOrder) => subOrder.id.toString()),
        order_status,
      };

      // Check if suborder can be changed
      if (
        order.sub_orders.some(
          (subOrder) => subOrder.order_status === ORDERSTATUS.shipped
        )
      ) {
        toast.warning("Suborders that are shipped can not be changed anymore");
      } else {
        await agent.Orders.updateMultiSubOrderStatus(updateValues);
      }

      // Show message
      toast.success(
        `Order ${order.order_code} is now ${order_status.toLowerCase()}`
      );
    } catch (error) {}
    cancelChangeOrder();
  };

  // Cancel changing an order
  const cancelChangeOrder = () => {
    setOrderToCancel(null);
    dispatch(openModal(false));
  };

  // Cancel changing an order
  const cancelShowSubOrder = () => {
    dispatch(openModal(false));
    dispatch(setSelectedSubOrder({ order: null, subOrder: null }));
  };

  // Request lists in bulk
  const bulkGenerateListsAndEmailHandler = async (supplier?: string) => {
    try {
      const { message } = await agent.Reports.emailMeListsForSelectedOrders({
        orders: selectedOrders.map((order) => order.id),
        sub_orders: true,
        supplier,
      });
      toast.success(message, { autoClose: 2000 });
    } catch (error) {
      toast.error(`${error}`);
    }
    dispatch(fetchOrdersAsync());
    dispatch(setSelectedOrders([]));
    dispatch(openModal(false));
  };

  const resetHandler = async () => {
    await dispatch(fetchOrdersAsync());
    await dispatch(fetchFilters());
  };
  //#endregion

  //#region REACT TABLE
  const columnHelper = createColumnHelper<Order>();
  const columns = [
    columnHelper.accessor((store) => store, {
      enableSorting: false,
      id: "isSelected",
      cell: (store) => (
        <MainSortableTableCheckBox
          {...{
            checked: store.row.getIsSelected(),
            disabled: !store.row.getCanSelect(),
            indeterminate: store.row.getIsSomeSelected(),
            onChange: store.row.getToggleSelectedHandler(),
          }}
        />
      ),
      header: ({ table }) => (
        <Fragment>
          <div
            data-tooltip-content={
              table.getIsAllRowsSelected() ? "De-select all" : "Select all"
            }
            data-tooltip-place="right"
            data-tooltip-variant={
              Object.entries(params).length > 0 ? "warning" : "info"
            }
            data-tooltip-id={String(selectedOrder?.id)}
          >
            <MainSortableTableCheckBox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
          <Tooltip id={String(selectedOrder?.id)} />
        </Fragment>
      ),
    }),
    columnHelper.accessor(({ id }) => id, {
      enableSorting: false,
      id: "readAndUpdate",
      cell: (item) => (
        <div className="flex gap-1.5 items-center h-6 mr-4">
          <div className="flex gap-1.5 items-center h-full mr-4">
            <MainTableRowActionRead
              detailHandler={() =>
                history.push(`/orders/${item.getValue()}`, true)
              }
            />
          </div>
        </div>
      ),
      header: () => null,
    }),
    columnHelper.accessor(({ promo_name }) => promo_name, {
      enableSorting: false,
      id: "promo_name",
      cell: (item) => item.getValue(),
      header: () => "promo",
    }),
    columnHelper.accessor(({ store }) => store.store_name, {
      id: "store_name",
      cell: (item) => item.getValue(),
      header: () => "store",
    }),
    columnHelper.accessor(({ store }) => store.external_store_id, {
      id: "external_store_id",
      cell: (item) => item.getValue(),
      header: () => "store ID",
    }),
    columnHelper.accessor(({ order_date }) => order_date, {
      id: "order_date",
      cell: (item) => item.getValue(),
      header: () => "order date",
    }),
    columnHelper.accessor(({ order_code }) => order_code, {
      id: "order_code",
      cell: (item) => item.getValue(),
      header: () => "order code",
    }),
    columnHelper.accessor(({ order_type }) => order_type, {
      id: "order_type",
      cell: (item) => item.getValue(),
      header: () => "order type",
    }),
    columnHelper.accessor(({ order_status }) => order_status, {
      id: "order_status",
      cell: (item) => item.getValue(),
      header: () => "order status",
    }),
  ];
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={CANCEL_ORDER_SURE} closeHandler={cancelChangeOrder}>
        <AreYouSure
          closeHandler={cancelChangeOrder}
          confirmHandler={() =>
            orderToCancel &&
            changeOrderStatusHandler(orderToCancel, "Cancelled")
          }
          title="Cancel order?"
          message={`Are you sure you want to cancel order ${orderToCancel?.order_code}`}
        />
      </MainModal>

      <MainModal
        type={CHOOSE_PICKLIST_SUPPLIER}
        closeHandler={cancelChangeOrder}
      >
        <SupplierSelect
          stock_portal={filters.stock_portal}
          closeHandler={cancelChangeOrder}
          confirmHandler={(supplier: string) =>
            bulkGenerateListsAndEmailHandler(supplier)
          }
          title="Choose Supplier?"
          message="Select the suppliers to generate picklists"
        />
      </MainModal>

      <MainModal
        type={SHOW_SUBORDER_INFO}
        closeHandler={cancelShowSubOrder}
        extraTopMargin={selectedOrder && selectedOrder?.order_items.length > 10}
      >
        <SubOrderDetail order={selectedOrder} subOrder={selectedSubOrder} />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader title={t(title)} disabled={loading} reset={resetHandler} />

        <OrdersFilterBar
          hideOrderstatusFilter
          hideOrdertypeFilter
          extensionButtons={
            <OrdersTableControlePanel hideSubOrderToggle hideHandle />
          }
        />

        <OrdersHandlingTable
          selectOrdersHandler={selectOrdersFromTableSelectionHandler}
          columns={columns}
          loading={loading}
          loaded={orders_loaded}
          orders={orders}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
